import { useMemo, useState } from "react";
import { mensagemDoConsole } from "../../nucleo/console";
import type { Ticket } from "../../nucleo/tickets";
import {
  camposAplicaveis,
  textoDoValor,
  useDefinicoesDeCampo,
  useGravarCampos,
  valorPreenchido,
  type DefinicaoDeCampo,
} from "../../nucleo/tickets-avancado";
import { Aviso, Botao, Cartao, Chip } from "../ui";
import { EntradaDeCampo } from "./EntradaDeCampo";

interface Props {
  ticket: Ticket;
  /** Quem gerencia o chamado edita; os demais só leem o que tem resposta. */
  podeEditar: boolean;
}

/** Campos personalizados do chamado: a equipe preenche, o cliente vê só os que são dele. */
export function CamposDoChamado({ ticket, podeEditar }: Props) {
  if (podeEditar) return <EditorDeCampos ticket={ticket} />;
  const respondidos = (ticket.campos ?? []).filter((campo) => valorPreenchido(campo.valor) || campo.valor === false || campo.valor === 0);
  if (respondidos.length === 0) return null;
  return (
    <Cartao titulo="Informações adicionais" className="mb-4" data-testid="campos-do-chamado">
      <dl className="grid gap-2 md:grid-cols-2">
        {respondidos.map((campo) => (
          <div key={campo.campo_id}>
            <dt className="campo__rotulo">{campo.rotulo}</dt>
            <dd>{textoDoValor(campo.valor)}</dd>
          </div>
        ))}
      </dl>
    </Cartao>
  );
}

function EditorDeCampos({ ticket }: { ticket: Ticket }) {
  const definicoes = useDefinicoesDeCampo();
  if (definicoes.isPending) return null;
  if (definicoes.isError || !definicoes.data) return null;
  const ativas = definicoes.data.filter((campo) => campo.ativo);
  if (ativas.length === 0) return null;
  const doServidor: Record<string, unknown> = Object.fromEntries(
    (ticket.campos ?? []).filter((campo) => campo.valor !== null && campo.valor !== undefined).map((campo) => [campo.campo_id, campo.valor]),
  );
  // Remonta o editor quando o servidor muda os valores, para o rascunho nunca ficar velho.
  return <Formulario key={JSON.stringify(doServidor)} ticket={ticket} definicoes={ativas} doServidor={doServidor} />;
}

function Formulario({
  ticket,
  definicoes,
  doServidor,
}: {
  ticket: Ticket;
  definicoes: DefinicaoDeCampo[];
  doServidor: Record<string, unknown>;
}) {
  const gravar = useGravarCampos(ticket.id);
  const [rascunho, setRascunho] = useState<Record<string, unknown>>(doServidor);
  const visiveis = useMemo(
    () => camposAplicaveis(definicoes, ticket.categoria, ticket.servico, rascunho),
    [definicoes, ticket.categoria, ticket.servico, rascunho],
  );
  if (visiveis.length === 0) return null;

  const mudancas: Record<string, unknown> = {};
  for (const campo of visiveis) {
    const antes = doServidor[campo.id] ?? null;
    const depois = rascunho[campo.id] ?? null;
    if (JSON.stringify(antes) !== JSON.stringify(depois)) mudancas[campo.id] = depois;
  }
  const pendente = visiveis.find((campo) => campo.obrigatorio && !valorPreenchido(rascunho[campo.id]) && rascunho[campo.id] !== false && rascunho[campo.id] !== 0);
  const sujo = Object.keys(mudancas).length > 0;

  return (
    <Cartao titulo="Campos do chamado" className="mb-4" data-testid="campos-do-chamado">
      <form
        onSubmit={(evento) => {
          evento.preventDefault();
          if (sujo && !pendente) gravar.mutate(mudancas);
        }}
      >
        <div className="grid gap-3 md:grid-cols-2">
          {visiveis.map((campo) => (
            <div key={campo.id}>
              <EntradaDeCampo
                campo={campo}
                valor={rascunho[campo.id] ?? null}
                aoMudar={(valor) => setRascunho((atual) => ({ ...atual, [campo.id]: valor }))}
              />
              {!campo.visivel_ao_cliente ? <Chip estado="neutro">Só equipe</Chip> : null}
            </div>
          ))}
        </div>
        {pendente && sujo ? <Aviso tom="atencao" className="mt-3">Preencha o campo “{pendente.rotulo}” para salvar.</Aviso> : null}
        {gravar.isError ? <Aviso tom="erro" className="mt-3">{mensagemDoConsole(gravar.error)}</Aviso> : null}
        <Botao type="submit" className="mt-3" disabled={!sujo || Boolean(pendente) || gravar.isPending}>
          {gravar.isPending ? "Salvando…" : "Salvar campos"}
        </Botao>
      </form>
    </Cartao>
  );
}
